import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import { Link, type LinkProps } from 'react-router-dom';
import { buttonClasses } from './buttonClasses';
import { clickGuard, inertAnchorProps, inertState, splitRouterProps } from './inertControl';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, sets aria-busy and swallows clicks. Focus stays put. */
  loading?: boolean;
  disabled?: boolean;
  /** Icon before the label (decorative; the label names the control). */
  leadingIcon?: ReactNode;
  /** Icon after the label (decorative). */
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
}

export type ButtonAsButtonProps = ButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps> & {
    href?: undefined;
    to?: undefined;
  };

export type ButtonAsLinkProps = ButtonOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps> & {
    href: string;
    to?: undefined;
  };

export type ButtonAsRouterLinkProps = ButtonOwnProps &
  Omit<LinkProps, keyof ButtonOwnProps> & { href?: undefined };

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps | ButtonAsRouterLinkProps;

type AriaDisabledProp = { 'aria-disabled'?: boolean | 'true' | 'false' };

/**
 * Pill button. Renders a <button>, an <a> when `href` is given, or a router
 * Link when `to` is given (as IconButton does).
 *
 * `disabled` on a <button> is the native attribute (not focusable, not
 * clickable). On an <a> there is no such attribute, so the link loses its
 * href and tab stop and says aria-disabled. `loading` keeps the control
 * focusable (the person who just pressed it does not lose their place) but
 * marks it busy and ignores clicks; a loading link also drops its href, so a
 * middle click, ctrl click or the context menu cannot open it either, and
 * keeps its link role and tab stop explicitly. A caller's
 * aria-disabled="true" refuses the same way, without the busy state. An
 * inert router link draws a plain <a>. The guard and the inert link are
 * IconButton's, from inertControl.ts.
 */
/** The label and icon slots every Button look shares. */
export function ButtonContent({
  loading = false,
  leadingIcon,
  trailingIcon,
  children,
}: Pick<ButtonOwnProps, 'loading' | 'leadingIcon' | 'trailingIcon' | 'children'>) {
  return (
    <>
      {loading ? (
        <LuLoaderCircle
          className="ee-button__icon animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : leadingIcon ? (
        <span className="ee-button__icon" aria-hidden="true">
          {leadingIcon}
        </span>
      ) : null}
      {children != null && <span className="ee-button__label">{children}</span>}
      {trailingIcon && !loading ? (
        <span className="ee-button__icon" aria-hidden="true">
          {trailingIcon}
        </span>
      ) : null}
    </>
  );
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      leadingIcon,
      trailingIcon,
      fullWidth = false,
      className,
      children,
      onClick,
      ...rest
    } = props;

    const ariaDisabled = (rest as AriaDisabledProp)['aria-disabled'];
    const { inert } = inertState({ disabled, loading, ariaDisabled });

    const content = (
      <ButtonContent loading={loading} leadingIcon={leadingIcon} trailingIcon={trailingIcon}>
        {children}
      </ButtonContent>
    );
    const shared = {
      className: buttonClasses({ variant, size, fullWidth, loading, className }),
      'aria-busy': loading || undefined,
      onClick: clickGuard(inert, onClick as ((e: MouseEvent<HTMLElement>) => void) | undefined),
    };

    if (rest.to !== undefined || typeof rest.href === 'string') {
      const { router, dom } = splitRouterProps(
        rest as Partial<LinkProps> & AnchorHTMLAttributes<HTMLAnchorElement>,
      );
      const { href, ...anchorRest } = dom;
      if (router.to !== undefined && !inert) {
        const linkProps = { ...anchorRest, ...shared, ...router, to: router.to };
        return (
          <Link {...linkProps} ref={ref as Ref<HTMLAnchorElement>}>
            {content}
          </Link>
        );
      }
      const anchorProps = {
        ...anchorRest,
        ...shared,
        ...inertAnchorProps({
          disabled,
          loading,
          ariaDisabled,
          href,
          role: anchorRest.role,
          tabIndex: anchorRest.tabIndex,
        }),
      };
      return (
        <a {...anchorProps} ref={ref as Ref<HTMLAnchorElement>}>
          {content}
        </a>
      );
    }

    const { type = 'button', ...buttonRest } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
    const buttonProps = {
      ...buttonRest,
      ...shared,
      type,
      disabled,
      'aria-disabled': loading ? true : buttonRest['aria-disabled'],
    };
    return (
      <button {...buttonProps} ref={ref as Ref<HTMLButtonElement>}>
        {content}
      </button>
    );
  },
);

export interface ButtonLookProps
  extends Pick<
    ButtonOwnProps,
    'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'fullWidth' | 'children'
  > {
  className?: string;
}

/**
 * The Button's look on a plain <span>, for a card whose WHOLE surface is the
 * link (a router Link or an <a>) and which shows its action as a button.
 * Not a control: no role, no tab stop, no click handling, so no control
 * sits inside the link (the link is the one tab stop, and its accessible
 * name includes this label). Same classes and slots as Button, from the
 * same helpers, so the two cannot drift. Hover styles key off the card
 * (`.ee-hub-card:hover .ee-hub-card__action`), not the span. Use Button for
 * anything that is itself pressed.
 */
export function ButtonLook({
  variant,
  size,
  fullWidth,
  className,
  leadingIcon,
  trailingIcon,
  children,
}: ButtonLookProps) {
  return (
    <span className={buttonClasses({ variant, size, fullWidth, className })}>
      <ButtonContent leadingIcon={leadingIcon} trailingIcon={trailingIcon}>
        {children}
      </ButtonContent>
    </span>
  );
}
